import React, { useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Grid,
    IconButton,
    InputAdornment,
    OutlinedInput,
    Snackbar,
    Tooltip,
    Typography,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

interface ClientCreateResponseDialogProps {
    open: boolean;
    data: ClientCreateResult | null;
    onClose: () => void;
}

const ClientCreateResponseDialog: React.FC<ClientCreateResponseDialogProps> = ({ open, data, onClose }) => {
    const [showSecret, setShowSecret] = useState(false);
    const [acknowledged, setAcknowledged] = useState(false);
    const [validationError, setValidationError] = useState('');
    const [copied, setCopied] = useState(false);

    const redirectUris = useMemo(() => {
        if (!data?.redirect_uri) {
            return [];
        }
        if (Array.isArray(data.redirect_uri)) {
            return data.redirect_uri;
        }
        return data.redirect_uri
            .split(',')
            .map((item) => item.trim())
            .filter(Boolean);
    }, [data]);

    const grants = useMemo(() => {
        if (!data?.grant_types) {
            return [];
        }
        return Array.isArray(data.grant_types)
            ? data.grant_types
            : data.grant_types.split(' ').map((item) => item.trim()).filter(Boolean);
    }, [data]);

    const scopes = useMemo(() => {
        if (!data?.scope) {
            return [];
        }
        return Array.isArray(data.scope)
            ? data.scope
            : data.scope.split(' ').map((item) => item.trim()).filter(Boolean);
    }, [data]);

    const handleAttemptClose = () => {
        if (!acknowledged) {
            setValidationError('Debes confirmar que entiendes que el secret no se mostrará nuevamente.');
            return;
        }

        setValidationError('');
        setAcknowledged(false);
        setShowSecret(false);
        onClose();
    };

    const handleCopy = async () => {
        if (!data?.password) {
            return;
        }
        await navigator.clipboard.writeText(data.password);
        setCopied(true);
    };

    return (
        <>
            <Dialog open={open} onClose={handleAttemptClose} maxWidth='md' fullWidth>
                <DialogTitle>Cliente creado</DialogTitle>
                <DialogContent dividers>
                    {data && (
                        <Grid container spacing={1.5}>
                            <Grid size={4}><Typography fontWeight={700}>Client ID</Typography></Grid>
                            <Grid size={8}><Typography>{data.client_id || '-'}</Typography></Grid>

                            <Grid size={4}><Typography fontWeight={700}>Nombre</Typography></Grid>
                            <Grid size={8}><Typography>{data.name}</Typography></Grid>

                            <Grid size={4}><Typography fontWeight={700}>Owner ID</Typography></Grid>
                            <Grid size={8}><Typography>{String(data.owner_id ?? '-')}</Typography></Grid>

                            <Grid size={4}><Typography fontWeight={700}>Grant types</Typography></Grid>
                            <Grid size={8}><Typography>{grants.join(', ') || '-'}</Typography></Grid>

                            <Grid size={4}><Typography fontWeight={700}>Scope</Typography></Grid>
                            <Grid size={8}><Typography>{scopes.join(' ') || '-'}</Typography></Grid>

                            <Grid size={4}><Typography fontWeight={700}>Redirect URI</Typography></Grid>
                            <Grid size={8}>
                                {redirectUris.length > 0 ? (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                        {redirectUris.map((uri) => (
                                            <Typography key={uri} component='code'>{uri}</Typography>
                                        ))}
                                    </Box>
                                ) : (
                                    <Typography>-</Typography>
                                )}
                            </Grid>

                            <Grid size={4}><Typography fontWeight={700}>Secret</Typography></Grid>
                            <Grid size={8}>
                                <OutlinedInput
                                    value={data.password || ''}
                                    type={showSecret ? 'text' : 'password'}
                                    fullWidth
                                    readOnly
                                    endAdornment={
                                        <InputAdornment position='end'>
                                            <Tooltip title={showSecret ? 'Ocultar' : 'Mostrar'}>
                                                <IconButton onClick={() => setShowSecret((value) => !value)} edge='end'>
                                                    {showSecret ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title='Copiar secret'>
                                                <IconButton onClick={handleCopy} edge='end'>
                                                    <ContentCopyIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </InputAdornment>
                                    }
                                />
                                <Typography variant='caption' color='warning.main'>
                                    Este secret solo se muestra en esta ocasión y no podrá recuperarse después.
                                </Typography>
                            </Grid>
                        </Grid>
                    )}

                    <Box sx={{ mt: 2 }}>
                        <FormControlLabel
                            control={<Checkbox checked={acknowledged} onChange={(event) => setAcknowledged(event.target.checked)} />}
                            label='Entiendo que debo copiar y resguardar el secret ahora, porque no se volverá a mostrar.'
                        />
                    </Box>

                    {validationError && (
                        <Alert severity='warning' sx={{ mt: 1 }}>
                            {validationError}
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button variant='contained' onClick={handleAttemptClose}>Cerrar</Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                open={copied}
                autoHideDuration={2000}
                onClose={() => setCopied(false)}
            >
                <Alert severity='success' onClose={() => setCopied(false)} sx={{ width: '100%' }}>
                    Secret copiado al portapapeles.
                </Alert>
            </Snackbar>
        </>
    );
};

export { ClientCreateResponseDialog };
